import React, { useState, useEffect, useMemo } from "react";
import {
  Badge,
  Card,
  CardBody,
  Input,
  Button,
} from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { getCourseEnrollmentList } from "action/batch";
import CommonTable from "components/CommonComponents/ReactTable/Table";
import { DEFAULT_RECORDS_PER_PAGE, COURSE_CONFIG } from "components/constant";
import { useLocation, useParams } from "react-router-dom";
import { urlDecodedId } from "components/CommonComponents/encodeDecodeFunction";
import AttendanceRightPanel from "components/CommonComponents/AttendanceRightPanel";
const EnrolledStudentsTab = ({ courseID, batchId, courseName, onViewArtifacts, accessType }) => {
  const authData = JSON.parse(localStorage.getItem("p") || "{}");
  const role = authData?.role;
  const config = COURSE_CONFIG[role] || {};
  const dispatch = useDispatch();
  const location = useLocation();
  const [pageNum, setPageNum] = useState(1);
  const [searchState, setSearchState] = useState({
    searchName: "",
    searchEmail: "",
    searchMobile: "",
    filterParams: [],
  });

  const [isRightPanelOpen, setIsRightPanelOpen] = useState(false);
  const [selectedTrainee, setSelectedTrainee] = useState(null);

  const [listState, setListState] = useState({
    isLoading: true,
    error: "",
    noRecords: false,
    showLoaderOnClick: false,
  });

  const courseEnrollmentData = useSelector(
    (state) => state.batch.courseEnrollmentList,
  );
  const courseType = courseEnrollmentData?.data?.trainees?.[0]?.courseType;
  const isFreeOrRecorded = accessType?.toLowerCase() === "free" || courseType?.toLowerCase() === "recorded";

  const { id } = useParams();

  const batchIdParam = useMemo(() => {
    if (batchId) {
      return urlDecodedId(batchId);
    }
    if (location.pathname.includes("/batch/")) {
      return id ? urlDecodedId(id) : null;
    } else {
      const locationState = location?.state || {};
      const param = locationState?.batchId || "";
      return param ? urlDecodedId(param) : null;
    }
  }, [batchId, id, location.pathname, location.search, location.state]);

  const batchNameParam = useMemo(() => {
    const locationState = location.state || {};
    return (
      locationState.batchName ||
      new URLSearchParams(location.search).get("batchName") ||
      "-"
    );
  }, [location.search, location.state]);

  useEffect(() => {
    if (!batchIdParam) return;

    const filters = searchState.filterParams?.length
      ? searchState.filterParams
      : [];

    getCourseEnrollmentList(batchIdParam, dispatch, pageNum, filters);
  }, [dispatch, pageNum, searchState.filterParams, batchIdParam]);

  useEffect(() => {
    if (courseEnrollmentData?.code === 200) {
      setListState({
        isLoading: false,
        error: "",
        noRecords: traineeTableData?.length === 0,
        showLoaderOnClick: false,
      });
    } else if (courseEnrollmentData?.code || courseEnrollmentData?.message) {
      setListState({
        isLoading: false,
        error:
          courseEnrollmentData?.message ||
          "Failed to load enrolled students! Please try again.",
        noRecords: false,
        showLoaderOnClick: false,
      });
    }
  }, [courseEnrollmentData]);

  const buildFilterParams = () => {
    const filterParams = [];

    if (searchState.searchName?.trim()) {
      filterParams.push({
        field: "TraineeName",
        condition: "like",
        filterValues: [searchState.searchName.trim()],
      });
    }

    if (searchState.searchEmail?.trim()) {
      filterParams.push({
        field: "Email",
        condition: "like",
        filterValues: [searchState.searchEmail.trim()],
      });
    }

    if (searchState.searchMobile?.trim()) {
      filterParams.push({
        field: "Mobile",
        condition: "like",
        filterValues: [searchState.searchMobile.trim()],
      });
    }

    return filterParams.length > 0 ? JSON.stringify(filterParams) : [];
  };

  const handleSearch = () => {
    const newFilterParams = buildFilterParams();

    if (
      JSON.stringify(searchState.filterParams) !==
      JSON.stringify(newFilterParams)
    ) {
      setListState((prev) => ({
        ...prev,
        isLoading: true,
        showLoaderOnClick: true,
      }));

      setSearchState((prev) => ({
        ...prev,
        filterParams: newFilterParams,
      }));

      if (pageNum !== 1) {
        setPageNum(1);
      }
    }
  };

  const handleClear = () => {
    setListState((prev) => ({
      ...prev,
      isLoading: true,
    }));

    setSearchState({
      searchName: "",
      searchEmail: "",
      searchMobile: "",
      filterParams: [],
    });

    if (pageNum !== 1) {
      setPageNum(1);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  const traineeTableData = courseEnrollmentData?.data?.trainees?.map(
    (trainee, index) => ({
      ...trainee,
      srNo:
        (pageNum - 1) *
        (courseEnrollmentData?.data?.noOfRecordsPerPage ||
          DEFAULT_RECORDS_PER_PAGE) +
        index +
        1,
      traineeID:
        trainee.traineeID || trainee.id || trainee.userId || trainee.traineeId,
      enrollmentID: trainee.enrollmentID || "-",
      traineeId:
        trainee.traineeID ||
        trainee.traineeId ||
        trainee.id ||
        trainee.userId ||
        trainee.enrollmentID,
      traineeName: trainee.fullName || trainee.traineeName || "-",
      progress: trainee.progressValue || 0,
      email: trainee.email || trainee.traineeEmail || "-",
      mobile: trainee.mobile || trainee.traineeMobile || "-",
      enrolledOn: trainee.enrolledOn || "-",
      paymentStatus: trainee.paymentStatus || "-",
    }),
  );
  const handleViewArtifacts = (row) => {
    if (onViewArtifacts) {
      onViewArtifacts(row.original);
    }
  };

  const traineeColumns = [
    {
      Header: "Sr.No.",
      accessor: "srNo",
      centerAlign: true,
      minWidth: 75,
      maxWidth: 75,
    },
    {
      Header: "Enrollment ID",
      accessor: "enrollmentID",
      minWidth: 130,
      maxWidth: 130,
      Cell: ({ value }) => <div className="text-wrapper">{value}</div>,
    },

    {
      Header: "Trainee Name",
      accessor: "traineeName",
      minWidth: 140,
      maxWidth: 140,
      Cell: ({ value }) => <div className="text-wrapper">{value}</div>,
    },

    {
      Header: "Email",
      accessor: "email",
      minWidth: 220,
      maxWidth: 220,
      Cell: ({ value }) => <div className="text-wrapper">{value}</div>,
    },
    {
      Header: "Mobile",
      accessor: "mobile",
      centerAlign: true,
      minWidth: 130,
      maxWidth: 130,
      Cell: ({ value }) => <div className="text-wrapper">{value}</div>,
    },
    {
      Header: "Enrolled On",
      accessor: "enrolledOn",
      minWidth: 130,
      maxWidth: 130,
      Cell: ({ value }) => <div className="text-wrapper">{value}</div>,
    },
    ...(accessType?.toLowerCase() === "paid"
      ? [
        {
          Header: "Payment Status",
          accessor: "paymentStatus",
          centerAlign: true,
          minWidth: 135,
          maxWidth: 135,
          Cell: ({ value }) => (
            <Badge color={getPaymentBadgeColor(value)} pill>
              {value === "CAPTURED" ? "Received" : value || "-"}
            </Badge>
          ),
        }] : []),
    ...(config.showArtifacts && location.pathname.includes("/batch/")
      ? [
        {
          Header: "Artifacts",
          accessor: "enrollmentID",
          id: "artifacts",
          centerAlign: true,
          minWidth: 90,
          maxWidth: 90,
          Cell: ({ row }) => (
            <Button
              size="sm"
              className="btn-link p-0 no-box-shadow-outline"
              title="View Artifacts"
              onClick={() => handleViewArtifacts(row)}
            >
              <i className="fa fa-paperclip" />
            </Button>
          ),
        },
      ]
      : []),
    ...(courseType === "RECORDED"
      ? [
        {
          Header: "Learning Status",
          accessor: "courseProgress",
          minWidth: 160,
          maxWidth: 160,
          Cell: ({ value }) => {
            const totalLessons = value?.totalLessons || 0;
            const completedLessons = value?.completedLessons || 0;
            const progressPercentage = value?.lessonProgressPercent || 0;
            return (
              <div className={`course-progress ${isFreeOrRecorded && "lg-progress"}`}>
                <div className="course-progress-percentage  text-right text-muted-small my-1">
                  {progressPercentage}%
                </div>
                <div className={`course-progress-bar ${isFreeOrRecorded && "lg-bar "}mb-1`}>
                  <div
                    className="course-progress-bar-fill"
                    style={{
                      width: `${progressPercentage}%`,
                    }}
                  />
                </div>
                <div className="course-progress-lessons text-right text-muted-small my-1">
                  {completedLessons}/{totalLessons} lessons
                </div>
              </div>
            );
          },
        },
      ]
      : []),
    ...(courseType === "LIVE"
      ? [
        {
          Header: "Attendance",
          id: "schedule",
          centerAlign: true,
          minWidth: 90,
          maxWidth: 90,
          Cell: ({ row }) => (
            <Button
              size="sm"
              className="btn-link p-0 no-box-shadow-outline"
              title="View Session"
              onClick={() => {
                setSelectedTrainee(row.original);
                setIsRightPanelOpen(true);
              }}
            >
              <i className="fa fa-calendar-alt text-info" />
            </Button>
          ),
        },
      ]
      : []),
  ];
  const getPaymentBadgeColor = (status) => {
    switch (status) {
      case "CAPTURED":
        return "success";
      case "PENDING":
        return "warning";
      case "FAILED":
        return "danger";
      default:
        return "secondary";
    }
  };

  return (
    <Card>
      <CardBody>
        <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap flex-column flex-md-row">
          <div className="d-flex flex-wrap align-items-center filter-group">
            <Input
              type="text"
              placeholder="Search by trainee name"
              className="mr-2 mb-2 filter-input-large"
              value={searchState.searchName}
              onChange={(e) =>
                setSearchState((prev) => ({
                  ...prev,
                  searchName: e.target.value,
                }))
              }
              onKeyDown={handleKeyDown}
            />
            <Input
              type="text"
              placeholder="Search by trainee email"
              className="mr-2 mb-2 filter-input-large"
              value={searchState.searchEmail}
              onChange={(e) =>
                setSearchState((prev) => ({
                  ...prev,
                  searchEmail: e.target.value,
                }))
              }
              onKeyDown={handleKeyDown}
            />
            <Input
              type="text"
              placeholder="Search by trainee mobile"
              className="mr-2 mb-2 filter-input-large"
              value={searchState.searchMobile}
              onChange={(e) =>
                setSearchState((prev) => ({
                  ...prev,
                  searchMobile: e.target.value,
                }))
              }
              onKeyDown={handleKeyDown}
            />
            <Button
              color="default"
              onClick={handleSearch}
              className={`d-flex align-items-center mb-2 filter-btn btn-deep-blue ${listState.showLoaderOnClick ? "search-btn" : ""}`}
            >
              {listState.showLoaderOnClick ? (
                <i className="fa fa-spinner fa-spin loader" />
              ) : (
                <>
                  <i className="fa fa-search mr-1" /> Search
                </>
              )}
            </Button>
            {searchState.filterParams.length > 0 ? (
              <Button
                color="default"
                className="mb-2 filter-btn btn-simple btn-deep-blue"
                onClick={handleClear}
              >
                <i className="fa fa-times mr-1" /> Clear
              </Button>
            ) : null}
          </div>
        </div>

        <div className="mt-2 mb-3 mx-1">
          {courseEnrollmentData?.data?.totalRecords > 0 ? (
            searchState.filterParams.length > 0 ? (
              <span className="total-records">
                Showing {courseEnrollmentData?.data?.totalFilteredRecords} of{" "}
                {courseEnrollmentData?.data?.totalRecords} Records
              </span>
            ) : (
              <span className="total-records">
                Total Records : {courseEnrollmentData?.data?.totalRecords}
              </span>
            )
          ) : null}
        </div>

        {listState.isLoading ? (
          <div className="loaderContainer">
            <i className="fa fa-spinner fa-spin fa-2xl loader" />
          </div>
        ) : listState.error ? (
          <div className="loaderContainer error-style">
            <div className="error-icon">
              <i className="fa-solid fa-circle-exclamation" />
            </div>
            <span>{listState.error}</span>
          </div>
        ) : listState.noRecords ? (
          <div className="loaderContainer empty-style">
            <div className="empty-icon">
              <i className="fa-solid fa-circle-info" />
            </div>
            <span>No enrolled students found.</span>
          </div>
        ) : (
          <CommonTable
            columns={traineeColumns}
            data={traineeTableData || []}
            defaultPageSize={DEFAULT_RECORDS_PER_PAGE}
            activePage={pageNum}
            setPageNum={setPageNum}
            totalPages={Math.ceil(
              (courseEnrollmentData?.data?.totalFilteredRecords ||
                traineeTableData?.length) /
              (courseEnrollmentData?.data?.noOfRecordsPerPage ||
                DEFAULT_RECORDS_PER_PAGE),
            )}
            showPaginationBottom={true}
          />
        )}
      </CardBody>
      {/* Attendance panel */}
      <AttendanceRightPanel
        isOpen={isRightPanelOpen}
        onClose={() => {
          setIsRightPanelOpen(false);
          setSelectedTrainee(null);
        }}
        selectedTrainee={selectedTrainee}
        courseID={courseID}
        batchId={batchIdParam}
        courseName={courseName}
        batchName={batchNameParam}
      />
    </Card>
  );
};

export default EnrolledStudentsTab;
